/* eslint-env browser */
// Pure helpers behind the modern DataTable (no React, no reads or writes):
// column normalising (material-table names accepted), cell values, sorting,
// searching, export columns, phone digits and per-person table preferences.
// Exported so pages and tests can use the same rules as the table.

import { formatAddisDate, formatAddisTime, formatAddisDateTime } from '../../common/exportUtils';

export const EMPTY_TEXT = '—';

/**
 * Old English strings stored in capitals ("CUSTOMERS", "CANCELLATION
 * REASON") in sentence case for titles and print headings; Amharic,
 * Tigrinya and words that already have small letters stay as they are.
 */
export const calmCaps = (text) => {
  const s = String(text === null || text === undefined ? '' : text);
  if (!/[A-Z]/.test(s) || /[a-z]/.test(s)) return s;
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

// One collator for every text sort: 40,000 rows sort without building a new
// one per comparison; "Item 2" comes before "Item 10".
const COLLATOR = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

const NUMBER_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/** 12400 -> "12,400" (tabular digits come from the cell style); '' for a non-number. */
export const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? NUMBER_FORMAT.format(n) : String(value);
};

/**
 * Money the way settings ask: decimals from settings.decimal (default 2,
 * trailing zeros dropped), and the symbol before or after the amount
 * (settings.swipe_symbol) only when withSymbol is true.
 *   formatMoney(1240.5, settings) -> "1,240.5"; formatMoney(1240.5, settings, true) -> "1,240.5 ETB"
 */
export const formatMoney = (value, settings, withSymbol = false) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const decimals = settings && Number.isInteger(settings.decimal) ? settings.decimal : 2;
  const text = n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
  const sym = withSymbol && settings && settings.symbol ? String(settings.symbol) : '';
  if (!sym) return text;
  return settings.swipe_symbol ? `${text} ${sym}` : `${sym} ${text}`;
};

export const getByPath = (row, path) => {
  if (!row || path === undefined || path === null || path === '') return undefined;
  if (Object.prototype.hasOwnProperty.call(row, path)) return row[path];
  return String(path)
    .split('.')
    .reduce((obj, key) => (obj !== null && obj !== undefined && obj[key] !== undefined ? obj[key] : undefined), row);
};

/** Epoch ms from a number, a digit string or date text; null when it is not a time. */
export const toMs = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const s = String(value).trim();
  if (/^\d{9,}$/.test(s)) return Number(s);
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
};

const TYPE_ALIASES = {
  numeric: 'number',
  currency: 'money',
  bool: 'boolean',
};

const RIGHT_TYPES = ['number', 'money'];

/**
 * Columns as the table uses them. Accepts material-table names so a page can
 * move its column list across almost unchanged:
 *   sorting: false -> sortable: false, printExport: false -> print: false,
 *   type 'numeric' -> 'number', 'currency' -> 'money'; editable / initialEditValue
 *   / cellStyle / headerStyle are ignored (the table draws every cell itself).
 */
export const normalizeColumns = (columns) =>
  (Array.isArray(columns) ? columns : [])
    .filter(Boolean)
    .map((col, index) => {
      const type = TYPE_ALIASES[col.type] || col.type || 'text';
      const hasValue = !!col.field || typeof col.sortValue === 'function' || typeof col.customSort === 'function';
      return {
        ...col,
        id: col.id || (col.field ? String(col.field) : `col${index}`),
        type,
        align: col.align || (RIGHT_TYPES.includes(type) ? 'right' : 'left'),
        sortable: col.sortable !== undefined ? !!col.sortable : col.sorting === false ? false : hasValue,
        searchable:
          col.searchable !== undefined ? !!col.searchable : !!col.field || typeof col.searchValue === 'function',
        print: col.print !== undefined ? !!col.print : col.printExport !== false,
        hideable: col.hideable !== undefined ? !!col.hideable : index > 0,
      };
    });

/** The words a lookup column shows for a stored value (the value itself when there is no entry). */
export const lookupText = (col, raw) => {
  if (!col || !col.lookup || raw === null || raw === undefined) return raw;
  if (!Object.prototype.hasOwnProperty.call(col.lookup, raw)) return raw;
  const shown = col.lookup[raw];
  return typeof shown === 'string' || typeof shown === 'number' ? shown : raw;
};

/** The raw value of a column for one row (field path, or sortValue when there is no field). */
export const rawValue = (col, row) => getByPath(row, col.field);

/** Plain text of a cell, used for search and as the fallback export. */
export const plainText = (col, row, settings) => {
  if (typeof col.searchValue === 'function') {
    const v = col.searchValue(row);
    return v === null || v === undefined ? '' : String(v);
  }
  const raw = rawValue(col, row);
  if (raw === null || raw === undefined || raw === '') return '';
  if (col.lookup) return String(lookupText(col, raw));
  switch (col.type) {
    case 'date':
      return formatAddisDate(raw);
    case 'datetime':
      return formatAddisDateTime(raw);
    case 'time':
      return formatAddisTime(raw);
    case 'number':
      return formatNumber(raw);
    case 'money':
      return formatMoney(raw, settings);
    default:
      return typeof raw === 'object' ? '' : String(raw);
  }
};

const isBlank = (v) => v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));

const sortKeyOf = (col, row) => {
  if (typeof col.sortValue === 'function') return col.sortValue(row);
  const raw = rawValue(col, row);
  if (isBlank(raw)) return null;
  if (col.lookup) return lookupText(col, raw);
  switch (col.type) {
    case 'number':
    case 'money': {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }
    case 'date':
    case 'datetime':
    case 'time':
      return toMs(raw);
    case 'boolean':
      return raw ? 1 : 0;
    default:
      return raw;
  }
};

const compareKeys = (a, b) => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' || typeof b === 'boolean') return (a ? 1 : 0) - (b ? 1 : 0);
  return COLLATOR.compare(String(a), String(b));
};

/**
 * Rows sorted by one column; a stable sort, empty values last in both
 * directions. customSort wins over sortValue / field and is called the way
 * material-table called it: (a, b, 'row') ascending, (b, a, 'row', 'desc')
 * descending.
 */
export const sortRows = (rows, col, dir) => {
  const list = Array.isArray(rows) ? rows.slice() : [];
  if (!col) return list;
  const sign = dir === 'desc' ? -1 : 1;
  if (typeof col.customSort === 'function') {
    const desc = dir === 'desc';
    return list
      .map((row, index) => ({ row, index }))
      .sort((x, y) => (desc ? col.customSort(y.row, x.row, 'row', 'desc') : col.customSort(x.row, y.row, 'row')) || x.index - y.index)
      .map((x) => x.row);
  }
  return list
    .map((row, index) => ({ row, index, key: sortKeyOf(col, row) }))
    .sort((x, y) => {
      const xb = isBlank(x.key);
      const yb = isBlank(y.key);
      if (xb && yb) return x.index - y.index;
      if (xb) return 1;
      if (yb) return -1;
      return compareKeys(x.key, y.key) * sign || x.index - y.index;
    })
    .map((x) => x.row);
};

/**
 * Rows whose searchable, shown columns contain the query (case does not
 * matter; dates match the way they are shown, "25 Oct 2026"). Columns with
 * hidden: true (role gates) are never searched.
 */
export const searchRows = (rows, columns, query, settings) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return Array.isArray(rows) ? rows : [];
  const cols = (columns || []).filter((col) => !col.hidden && col.searchable);
  return (rows || []).filter((row) => cols.some((col) => plainText(col, row, settings).toLowerCase().includes(q)));
};

/**
 * Columns for exportUtils (exportCsv / printTable) from the table's columns:
 * role-hidden columns and export: false are left out; for print, print: false
 * too. The value is exportValue(row), else the lookup words, else the field
 * (dates stay raw so exportUtils writes them in Addis Ababa 12-hour time);
 * a yes/no column exports the words `yes` / `no`.
 */
export const exportColumnsOf = (columns, { forPrint = false, settings, yes = 'Yes', no = 'No' } = {}) =>
  (columns || [])
    .filter((col) => !col.hidden && col.export !== false && (!forPrint || col.print !== false))
    .filter((col) => col.field || typeof col.exportValue === 'function' || typeof col.searchValue === 'function')
    .map((col) => {
      const dateType = ['date', 'datetime', 'time'].includes(col.type) ? col.type : undefined;
      let value;
      if (typeof col.exportValue === 'function') value = col.exportValue;
      else if (col.lookup && col.field) value = (row) => lookupText(col, rawValue(col, row));
      else if (col.type === 'boolean' && col.field) value = (row) => (rawValue(col, row) ? yes : no);
      else if (!col.field) value = (row) => plainText(col, row, settings);
      return {
        title: typeof col.exportTitle === 'string' ? col.exportTitle : typeof col.title === 'string' ? col.title : String(col.field || col.id),
        field: col.field,
        value,
        type: col.exportType || (typeof col.exportValue === 'function' ? undefined : dateType),
        align: col.align === 'right' ? 'right' : undefined,
      };
    });

// ── Phone numbers (owner rule: inputs take plain digits, 912345678) ─────────

/** Digits only. */
export const onlyDigits = (value) => String(value === null || value === undefined ? '' : value).replace(/\D/g, '');

/**
 * Whatever was typed or pasted (+251 91 234 5678, 0912345678, 251912345678,
 * 00251 91…) -> "912345678" (at most 9 digits). A leading + or 00 always
 * means a country code, so a half-pasted "+251 91 2" is "912".
 */
export const toNineDigits = (raw) => {
  const s = String(raw === null || raw === undefined ? '' : raw).trim();
  let d = onlyDigits(s);
  if (/^(\+|00)/.test(s)) {
    if (d.startsWith('00')) d = d.slice(2);
    if (d.startsWith('251')) d = d.slice(3);
  } else if (d.length > 9 && d.startsWith('251')) d = d.slice(3);
  d = d.replace(/^0+/, '');
  return d.slice(0, 9);
};

/** "912345678" -> "+251912345678" (the stored form); '' when not 9 digits. */
export const toEthiopianE164 = (nine) => {
  const d = toNineDigits(nine);
  return d.length === 9 ? `+251${d}` : '';
};

/** A valid Ethiopian mobile: 9 digits starting with 9 or 7. */
export const isNineDigitMobile = (value) => /^[79]\d{8}$/.test(String(value || ''));

/**
 * A phone number for reading: no spaces, as it is stored and as staff paste
 * it into MicroSIP - "+251945006863" (owner, 29 Oct 2026: "+251 94 500 6863"
 * is a US-style format; no spaces in the portal, format and view). Anything
 * that is not a phone number (a masked or placeholder text) is unchanged.
 */
export const formatPhoneDisplay = (mobile) => {
  if (mobile === null || mobile === undefined || mobile === '') return '';
  return compactPhone(mobile);
};

/**
 * A phone number with no spaces, for pasting into a dialler (MicroSIP takes
 * "+251964540448", not "+251 96 454 0448"): spaces, dashes, dots and brackets
 * go, a leading + stays. Anything else comes back as it was.
 */
export const compactPhone = (value) => {
  const s = String(value === null || value === undefined ? '' : value).trim();
  if (!/^\+?[\d\s\-().]{7,}$/.test(s)) return s;
  return (s.startsWith('+') ? '+' : '') + onlyDigits(s);
};

// "+251 96 454 0448", "+251-96-454-0448", "+251 964 540 448" ... in a text:
// always a + then the 251 code, then 9 digits with separators on ONE line (a
// match never takes the next line's or cell's digits).
const SPACED_ETHIOPIAN_PHONE = /\+251(?:[ \t\u00A0\-.]*\d){9}(?!\d)/g;

/**
 * Text with every spaced Ethiopian number made compact ("Abebe +251 91 234
 * 5678" -> "Abebe +251912345678"); null when nothing in it changes.
 */
export const compactPhonesIn = (text) => {
  const s = String(text === null || text === undefined ? '' : text);
  const out = s.replace(SPACED_ETHIOPIAN_PHONE, (m) => `+${onlyDigits(m)}`);
  return out === s ? null : out;
};

/**
 * The national digits of a search query: a leading 251 (a local number
 * never starts with 2) and leading zeros dropped, so a half-typed
 * "+251 912" is "912".
 */
export const queryDigits = (query) => {
  let d = onlyDigits(query);
  if (d.startsWith('251')) d = d.slice(3);
  return d.replace(/^0+/, '');
};

/**
 * Does a phone number match what someone typed? Compares the national
 * digits, so "0912 34", "91234" and "+251 912" all find +251912345678.
 */
export const phoneMatches = (mobile, query) => {
  const q = queryDigits(query);
  if (q.length < 3) return false;
  const local = toNineDigits(mobile);
  return !!local && local.includes(q);
};

// ── Per-person table preferences (browser storage only, never shared) ──────

const prefKey = (prefsKey, name, uid) => `dd_table_${prefsKey}_${name}_${uid || 'anon'}`;

export const readTablePref = (prefsKey, name, uid, fallback) => {
  if (!prefsKey) return fallback;
  try {
    const raw = window.localStorage.getItem(prefKey(prefsKey, name, uid));
    return raw === null ? fallback : JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

export const writeTablePref = (prefsKey, name, uid, value) => {
  if (!prefsKey) return;
  try {
    window.localStorage.setItem(prefKey(prefsKey, name, uid), JSON.stringify(value));
  } catch (e) {
    // private window or blocked storage: the choice lasts for this visit only
  }
};
